import type { CalendarEvent, CreateCalendarEventInput, TaskSummary } from '@pm/schemas';
import {
  Avatar,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  ErrorState,
  Field,
  Input,
  Label,
  PageHeader,
  Segmented,
  Select,
  Skeleton,
  Textarea,
} from '@pm/ui';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ListTodo,
  MapPin,
  Plus,
  Repeat,
  Trash2,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { ContextSelect, UsersMultiPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDateTime } from '@/lib/format';
import { useCalendar, useInvalidating, useTeams } from '@/lib/queries';
import { useUpdateViewPrefs, useViewPrefs } from '@/lib/view-prefs';
import { useToast } from '@/lib/toast';

type View = 'month' | 'week';
const RECURRENCE = [
  { value: '', label: 'Não se repete' },
  { value: 'FREQ=DAILY', label: 'Diariamente' },
  { value: 'FREQ=WEEKLY', label: 'Semanalmente' },
  { value: 'FREQ=WEEKLY;INTERVAL=2', label: 'A cada duas semanas' },
  { value: 'FREQ=MONTHLY', label: 'Mensalmente' },
];

const toLocalInput = (iso: string) => format(parseISO(iso), "yyyy-MM-dd'T'HH:mm");

/**
 * SPEC §34 — calendário com compromissos (agendas com clientes e reuniões do time; vão para o
 * Google/cPanel de quem conectou) e entregas (prazos das demandas; só aqui, nunca sincronizam).
 * Seleção, visão e "só meus" ficam salvos nas preferências.
 */
export function CalendarPage() {
  const toast = useToast();
  const { prefs } = useViewPrefs();
  const updatePrefs = useUpdateViewPrefs();
  const { show, view, mine } = prefs.calendar;
  const setView = (v: View) => updatePrefs({ calendar: { view: v } });
  const showAppointments = show !== 'deliveries';
  const showDeliveries = show !== 'appointments';
  const [cursor, setCursor] = useState(() => new Date());
  const [editing, setEditing] = useState<CalendarEvent | 'new' | null>(null);
  const [newDate, setNewDate] = useState<Date | null>(null);
  const [removing, setRemoving] = useState<CalendarEvent | null>(null);

  const range = useMemo(() => {
    if (view === 'week') {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      return { start: s, end: addDays(s, 7) };
    }
    return {
      start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
      end: addDays(endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }), 1),
    };
  }, [view, cursor]);
  const { data, isLoading, isError, error, refetch } = useCalendar({
    from: range.start.toISOString(),
    to: range.end.toISOString(),
    mine: mine || undefined,
    include_tasks: showDeliveries,
  });
  const remove = useInvalidating(
    (id: string) => api.delete(`/calendar/events/${id}`),
    [['calendar']],
  );

  const days = useMemo(() => {
    const out: Date[] = [];
    for (let d = range.start; d < range.end; d = addDays(d, 1)) out.push(d);
    return out;
  }, [range]);
  const eventsOn = (d: Date) =>
    (data?.events ?? []).filter(
      (e) =>
        isSameDay(parseISO(e.starts_at), d) ||
        (e.all_day && parseISO(e.starts_at) <= d && parseISO(e.ends_at) > d),
    );
  const tasksOn = (d: Date) =>
    (data?.tasks ?? []).filter((t) => t.due_at && isSameDay(parseISO(t.due_at), d));

  return (
    <div className="flex flex-col gap-gap-lg">
      <PageHeader
        title="Calendário"
        subtitle={format(cursor, view === 'month' ? "MMMM 'de' yyyy" : "'Semana de' d 'de' MMMM", {
          locale: ptBR,
        })}
        actions={
          <>
            <Segmented
              ariaLabel="O que mostrar"
              value={show}
              onChange={(v) => updatePrefs({ calendar: { show: v } })}
              options={[
                { value: 'all', label: 'Tudo' },
                {
                  value: 'appointments',
                  label: (
                    <>
                      <CalendarClock /> Compromissos
                    </>
                  ),
                },
                {
                  value: 'deliveries',
                  label: (
                    <>
                      <ListTodo /> Entregas
                    </>
                  ),
                },
              ]}
            />
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={mine}
              className={cn(mine && 'bg-accent-soft text-accent')}
              onClick={() => updatePrefs({ calendar: { mine: !mine } })}
            >
              <Users /> Só meus
            </Button>
            <Segmented
              ariaLabel="Visualização"
              value={view}
              onChange={setView}
              options={[
                { value: 'month', label: 'Mês' },
                { value: 'week', label: 'Semana' },
              ]}
            />
            <span className="inline-flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Anterior"
                onClick={() =>
                  setCursor(view === 'month' ? addMonths(cursor, -1) : addDays(cursor, -7))
                }
              >
                <ChevronLeft />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())}>
                Hoje
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Próximo"
                onClick={() =>
                  setCursor(view === 'month' ? addMonths(cursor, 1) : addDays(cursor, 7))
                }
              >
                <ChevronRight />
              </Button>
            </span>
            <Button
              variant="primary"
              onClick={() => {
                setNewDate(null);
                setEditing('new');
              }}
            >
              <Plus /> Compromisso
            </Button>
          </>
        }
      />
      {isError ? <ErrorState message={error.message} onRetry={() => void refetch()} /> : null}
      {isLoading && !data ? (
        <Skeleton className="h-[480px] rounded-md" />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-surface-1">
          <div className="grid grid-cols-7 border-b border-border-strong bg-bg">
            {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((d) => (
              <div
                key={d}
                className="px-2 py-1.5 font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d) => {
              const evs = showAppointments ? eventsOn(d) : [];
              const tks = showDeliveries ? tasksOn(d) : [];
              const isToday = isSameDay(d, new Date());
              const outside = view === 'month' && !isSameMonth(d, cursor);
              return (
                <div
                  key={d.toISOString()}
                  className={cn(
                    'flex flex-col gap-1 border-r border-b border-border p-1.5',
                    view === 'month' ? 'min-h-[110px]' : 'min-h-[420px]',
                    outside && 'bg-bg/60',
                    (d.getDay() === 0 || d.getDay() === 6) && 'bg-bg-elev/40',
                  )}
                  onDoubleClick={() => {
                    setNewDate(d);
                    setEditing('new');
                  }}
                >
                  <span
                    className={cn(
                      'self-end font-mono text-xs',
                      isToday
                        ? 'rounded-sm bg-accent px-1 text-text-inverse'
                        : outside
                          ? 'text-text-3'
                          : 'text-text-2',
                    )}
                  >
                    {format(d, 'd')}
                  </span>
                  {evs.map((e) => (
                    <button
                      key={`${e.id}-${e.starts_at}`}
                      type="button"
                      onClick={() => setEditing(e)}
                      className="flex items-center gap-1 truncate rounded-sm border border-accent-border bg-accent-soft px-1.5 py-0.5 text-left text-xs text-text-1 hover:border-accent"
                      title={e.title}
                    >
                      {!e.all_day ? (
                        <span className="font-mono text-[10px] text-accent">
                          {format(parseISO(e.starts_at), 'HH:mm')}
                        </span>
                      ) : null}
                      <span className="truncate">{e.title}</span>
                      {e.rrule ? (
                        <Repeat className="ml-auto size-2.5 shrink-0 text-text-3" />
                      ) : null}
                    </button>
                  ))}
                  {tks.map((t) => (
                    <TaskChip key={t.id} task={t} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editing ? (
        <EventDialog
          event={editing === 'new' ? null : editing}
          defaultDate={newDate}
          onClose={() => setEditing(null)}
          onDelete={(e) => {
            setEditing(null);
            setRemoving(e);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={() => setRemoving(null)}
        title={`Excluir ${removing?.title ?? ''}?`}
        description={removing?.rrule ? 'Toda a série será excluída.' : undefined}
        confirmLabel="Excluir"
        danger
        loading={remove.isPending}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => (toast.success('Compromisso excluído'), setRemoving(null)),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function TaskChip({ task }: { task: TaskSummary }) {
  return (
    <Link
      to={`/tasks/${task.id}`}
      className={cn(
        'flex items-center gap-1 truncate rounded-sm border border-border bg-surface-2 px-1.5 py-0.5 text-xs text-text-2 hover:text-text-1',
        task.is_overdue && 'border-danger/40',
        task.status === 'COMPLETED' && 'line-through opacity-60',
      )}
      title={task.title}
    >
      <span className="font-mono text-[10px] text-text-3">#{task.number}</span>
      <span className="truncate">{task.title}</span>
    </Link>
  );
}

function EventDialog({
  event,
  defaultDate,
  onClose,
  onDelete,
}: {
  event: CalendarEvent | null;
  defaultDate: Date | null;
  onClose: () => void;
  onDelete: (e: CalendarEvent) => void;
}) {
  const toast = useToast();
  const me = useMe();
  const { data: teams = [] } = useTeams();
  const base = defaultDate ?? new Date();
  const [title, setTitle] = useState(event?.title ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [allDay, setAllDay] = useState(event?.all_day ?? false);
  const [start, setStart] = useState(
    event
      ? toLocalInput(event.starts_at)
      : format(
          new Date(base.getFullYear(), base.getMonth(), base.getDate(), 9),
          "yyyy-MM-dd'T'HH:mm",
        ),
  );
  const [end, setEnd] = useState(
    event
      ? toLocalInput(event.ends_at)
      : format(
          new Date(base.getFullYear(), base.getMonth(), base.getDate(), 10),
          "yyyy-MM-dd'T'HH:mm",
        ),
  );
  const [contextId, setContextId] = useState<string | null>(event?.context?.id ?? null);
  const [location, setLocation] = useState(event?.location ?? '');
  const [meetingUrl, setMeetingUrl] = useState(event?.meeting_url ?? '');
  const [rrule, setRrule] = useState(event?.rrule ?? '');
  const [participants, setParticipants] = useState<string[]>(
    event?.participants.map((p) => p.id) ?? [me.user.id],
  );
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const readOnly = Boolean(event && !event.can_edit);
  const save = useInvalidating(
    (input: CreateCalendarEventInput) =>
      event
        ? api.patch<CalendarEvent>(`/calendar/events/${event.id}`, input)
        : api.post<CalendarEvent>('/calendar/events', input),
    [['calendar']],
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={event ? (readOnly ? 'Compromisso' : 'Editar compromisso') : 'Novo compromisso'}
        size="lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            const s = allDay ? new Date(`${start.slice(0, 10)}T00:00:00`) : new Date(start);
            const en = allDay
              ? new Date(new Date(`${end.slice(0, 10)}T00:00:00`).getTime() + 86_400_000)
              : new Date(end);
            save.mutate(
              {
                title,
                description: description || null,
                starts_at: s.toISOString(),
                ends_at: en.toISOString(),
                all_day: allDay,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
                location: location || null,
                meeting_url: meetingUrl || null,
                context_id: contextId,
                rrule: rrule || null,
                participant_user_ids: participants,
                team_ids: teamIds,
              },
              {
                onSuccess: () => (
                  toast.success(event ? 'Compromisso atualizado' : 'Compromisso criado'),
                  onClose()
                ),
                onError: (err) => toast.error(err),
              },
            );
          }}
        >
          <Field label="Título" htmlFor="ev-title">
            <Input
              id="ev-title"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={readOnly}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Início" htmlFor="ev-start">
              <Input
                id="ev-start"
                type={allDay ? 'date' : 'datetime-local'}
                mono
                value={allDay ? start.slice(0, 10) : start}
                onChange={(e) => setStart(allDay ? `${e.target.value}T09:00` : e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="Fim" htmlFor="ev-end">
              <Input
                id="ev-end"
                type={allDay ? 'date' : 'datetime-local'}
                mono
                value={allDay ? end.slice(0, 10) : end}
                onChange={(e) => setEnd(allDay ? `${e.target.value}T10:00` : e.target.value)}
                disabled={readOnly}
              />
            </Field>
          </div>
          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={allDay}
                onCheckedChange={(v) => setAllDay(v === true)}
                disabled={readOnly}
              />{' '}
              Dia inteiro
            </label>
            <Field label="Repetição" htmlFor="ev-rrule">
              <Select
                id="ev-rrule"
                value={RECURRENCE.some((r) => r.value === rrule) ? rrule : 'custom'}
                onChange={(e) => setRrule(e.target.value === 'custom' ? rrule : e.target.value)}
                disabled={readOnly}
                className="h-control-sm text-sm"
              >
                {RECURRENCE.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
                {!RECURRENCE.some((r) => r.value === rrule) ? (
                  <option value="custom">Personalizada: {rrule}</option>
                ) : null}
              </Select>
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ev-ctx">Contexto / cliente</Label>
              <ContextSelect
                id="ev-ctx"
                value={contextId}
                onChange={setContextId}
                disabled={readOnly}
              />
            </div>
            <Field label="Local" htmlFor="ev-loc">
              <Input
                id="ev-loc"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={readOnly}
                placeholder="Sala, endereço…"
              />
            </Field>
          </div>
          <Field label="Link da reunião" htmlFor="ev-url">
            <Input
              id="ev-url"
              type="url"
              mono
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
              disabled={readOnly}
              placeholder="https://meet…"
            />
          </Field>
          <Field label="Descrição" htmlFor="ev-desc">
            <Textarea
              id="ev-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={readOnly}
              className="min-h-[64px]"
            />
          </Field>
          <div className="flex flex-col gap-1.5">
            <Label>Participantes</Label>
            {readOnly ? (
              <div className="flex flex-wrap gap-2">
                {event?.participants.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1.5 text-sm">
                    <Avatar name={p.name} size="xs" />
                    {p.name}
                  </span>
                ))}
              </div>
            ) : (
              <UsersMultiPicker value={participants} onChange={setParticipants} />
            )}
            {!readOnly && teams.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-text-3">Times em lote:</span>
                {teams.map((t) => (
                  <label
                    key={t.id}
                    className="flex items-center gap-1.5 rounded border border-border px-2 py-0.5"
                  >
                    <Checkbox
                      checked={teamIds.includes(t.id)}
                      onCheckedChange={(v) =>
                        setTeamIds(v ? [...teamIds, t.id] : teamIds.filter((x) => x !== t.id))
                      }
                    />
                    {t.name}{' '}
                    <span className="font-mono text-xs text-text-3">{t.members.length}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
          {event ? (
            <p className="flex flex-wrap items-center gap-3 font-mono text-xs text-text-3">
              <span>
                {fmtDateTime(event.starts_at)} → {fmtDateTime(event.ends_at)}
              </span>
              {event.location ? (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="size-3" />
                  {event.location}
                </span>
              ) : null}
              {event.meeting_url ? (
                <a
                  href={event.meeting_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  <ExternalLink className="size-3" />
                  entrar
                </a>
              ) : null}
              {event.created_by ? <span>por {event.created_by.name}</span> : null}
            </p>
          ) : null}
          <DialogFooter>
            {event?.can_edit ? (
              <Button
                variant="ghost"
                type="button"
                className="mr-auto text-danger"
                onClick={() => onDelete(event)}
              >
                <Trash2 /> Excluir
              </Button>
            ) : null}
            <Button variant="ghost" type="button" onClick={onClose}>
              {readOnly ? 'Fechar' : 'Cancelar'}
            </Button>
            {!readOnly ? (
              <Button
                variant="primary"
                type="submit"
                loading={save.isPending}
                disabled={!title.trim()}
              >
                Salvar
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
